import { IconPlus } from '@developerz/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EmptyState } from '../components/EmptyState';
import { Field } from '../components/Field';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { PromptVarFormModal } from '../components/PromptVarFormModal';
import { PromptVarTable } from '../components/PromptVarTable';
import { QueryBoundary } from '../components/QueryBoundary';
import { Select } from '../components/Select';
import { Skeleton } from '../components/Skeleton';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { createAgentConfigEditor } from '../lib/agent-config-editor';
import { PROMPT_VAR_SCOPE_LABEL } from '../lib/prompt-var-scope';
import {
  type ApiPromptVar,
  deletePromptVar,
  PROMPT_VAR_SCOPES,
  type PromptVarAddress,
  type PromptVarScope,
  setPromptVar,
  usePromptVars,
} from '../lib/prompt-vars';
import { SESSION_TOKEN } from '../lib/session';

// Prompt vars (#52) — the human door onto `/v1/prompt-vars` + the MCP `config`
// verb's KV actions. CONTAINER: it owns the read + the scope filter, hands rows
// to <PromptVarTable/> and writes to the editor.
//
// The scope filter is the ROUTE's `?scope=` knob, part of the query key, so
// narrowing re-reads server-side. Filtering rows the client happens to hold would
// be the bug this app's CLAUDE.md names: the list is not exhaustively loaded.
//
// Every write is followed by a RE-READ. An edit that moves the address writes a
// DIFFERENT row (scope+ref+key is the address, on every surface) — the re-read is
// what shows both, rather than a patched row pretending one moved.

export function AccountPromptVars(props: { token?: string | null } = {}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session".
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const [scope, setScope] = createSignal<PromptVarScope | ''>('');
  const query = usePromptVars(
    () => (scope() === '' ? {} : { scope: scope() as PromptVarScope }),
    token,
  );
  const editor = createAgentConfigEditor<ApiPromptVar>({
    fallback: t('account_prompt_vars.write_failed'),
    onWritten: () => void query.refetch(),
  });

  const onSave = (at: PromptVarAddress, value: unknown): void =>
    editor.submit(() => setPromptVar(at, value, token));

  return (
    <section class="page account-prompt-vars">
      <PageHeader
        title={t('account_prompt_vars.title')}
        desc={t('account_prompt_vars.subtitle')}
        actions={
          <Show when={token !== null}>
            <Button onClick={() => editor.open()}>
              <IconPlus />
              {t('account_prompt_vars.add')}
            </Button>
          </Show>
        }
      />

      <Show
        when={token !== null}
        fallback={<EmptyState message={t('account_prompt_vars.no_session')} />}
      >
        <div class="filter-row">
          <Field label={t('account_prompt_vars.filter_scope')} id="prompt-var-filter-scope">
            {(a11y) => (
              <Select<PromptVarScope | ''>
                a11y={a11y}
                label={t('account_prompt_vars.filter_scope')}
                block
                value={scope()}
                options={[
                  { value: '', label: t('account_prompt_vars.filter_all') },
                  ...PROMPT_VAR_SCOPES.map((s) => ({
                    value: s,
                    label: t(PROMPT_VAR_SCOPE_LABEL[s]),
                  })),
                ]}
                onChange={setScope}
              />
            )}
          </Field>
        </div>

        {/* QueryBoundary, not a bare Show: loading must not read as "empty". */}
        <QueryBoundary
          query={query}
          isEmpty={(rows) => rows.length === 0}
          loading={<Skeleton height="14rem" />}
          empty={
            <EmptyState
              message={t('account_prompt_vars.empty')}
              ctaLabel={t('account_prompt_vars.add')}
              onCta={() => editor.open()}
            />
          }
        >
          {(rows) => (
            <Panel title={t('account_prompt_vars.list_title')}>
              <PromptVarTable rows={rows} onEdit={editor.open} onDelete={editor.askDelete} />
            </Panel>
          )}
        </QueryBoundary>
      </Show>

      <PromptVarFormModal
        target={editor.editing()}
        pending={editor.pending()}
        error={editor.error()}
        onSave={onSave}
        onClose={editor.close}
      />

      <ConfirmDialog
        open={editor.deleting() !== null}
        title={t('account_prompt_vars.delete_title', { key: editor.deleting()?.key ?? '' })}
        desc={t('account_prompt_vars.delete_desc')}
        cancelLabel={t('account_prompt_vars.cancel')}
        actionLabel={t('account_prompt_vars.delete')}
        destructive
        onCancel={editor.cancelDelete}
        onConfirm={() =>
          editor.confirmDelete((row) =>
            deletePromptVar({ scope: row.scope, ref: row.ref, key: row.key }, token),
          )
        }
      />
      <Toast message={editor.deleteError()} tone="error" onDismiss={editor.clearDeleteError} />
    </section>
  );
}
